import { useEffect, useState } from "react";
import { collection, getCountFromServer, query, where } from "firebase/firestore";

import { db } from "../lib/firebase";
import { firestoreErrorMessage } from "./useCollection";

/**
 * STATÜ KARTLARINDAKİ SAYILAR — CANLI SAYIM.
 *
 * ── NİÇİN BU KANCA VAR ─────────────────────────────────────────────────────
 * Stok Siparişi ve SSH ekranları statü kartlarındaki adedi `counters/stockOrders`
 * ve `counters/afterSales` dokümanlarındaki `byStatus` haritasından okuyordu.
 *
 * ★ O DOKÜMANLARI YAZAN HİÇBİR KOD YOK. `functions/` altında `counters/` yoluna
 * yapılan TEK BİR yazma bile bulunmuyor — sayaçlar göçten kalma, donmuş
 * rakamlardı. Sonuç: kart "Üretilecek — 172 kayıt" derken listede 7 kayıt
 * görünüyordu. Hiçbir yerde hata çıkmıyor; ekran sessizce yanlış sayı
 * gösteriyordu.
 *
 * ── NİÇİN SAYAÇ DOKÜMANI YERİNE SORGU ──────────────────────────────────────
 * Sayaç dokümanını tetikleyiciyle canlı tutmak mümkündü ama her yazmada ikinci
 * bir yazma demek ve sayaç ile gerçek arasında SAPMA İHTİMALİ kalıcı olarak
 * duruyor (bu projede `users.unreadNotificationCount` tam da böyle saptı ve
 * sonunda kaldırıldı). `getCountFromServer` sunucuda toplama yapar: dokümanları
 * indirmez, sonucu okunan doküman sayısı kadar değil, sabit ücretlendirilir ve
 * SAPAMAZ — çünkü türetilmiş bir kopya değil, sorgunun kendisidir.
 *
 * ── HATA DAVRANIŞI ─────────────────────────────────────────────────────────
 * Sayım başarısız olursa değer `null` kalır ve ekran "—" gösterir. Sıfır YAZMAZ:
 * "sayılamadı" ile "hiç kayıt yok" aynı şey değildir ve bu ekranlarda sıfır
 * görmek "iş bitti" demek olurdu.
 */
export interface StatusCountsState {
  /** statü kimliği → adet. Sayılamayan statü haritada BULUNMAZ. */
  counts: Record<string, number>;
  loading: boolean;
  error: string | null;
}

export function useStatusCounts(
  collectionName: string,
  statusField: string,
  statusIds: readonly string[],
  /** Değişince yeniden sayılır (ör. bir kayıt taşındıktan sonra). */
  refreshKey: unknown = 0,
): StatusCountsState {
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Dizi kimliği her render'da değişmesin diye içerikten anahtar üretilir.
  const key = statusIds.join("|");

  useEffect(() => {
    if (statusIds.length === 0) {
      setCounts({});
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      const out: Record<string, number> = {};
      let firstError: string | null = null;

      // Statü sayısı azdır (5-8); tek tek saymak paralel olarak yeterince hızlı.
      await Promise.all(
        statusIds.map(async (id) => {
          try {
            const snap = await getCountFromServer(
              query(collection(db, collectionName), where(statusField, "==", id)),
            );
            out[id] = snap.data().count;
          } catch (e) {
            // Tek statünün sayılamaması diğerlerini düşürmez; o kart "—" olur.
            if (!firstError) firstError = firestoreErrorMessage(e as never);
          }
        }),
      );

      if (cancelled) return;
      setCounts(out);
      setError(firstError);
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectionName, statusField, key, refreshKey]);

  return { counts, loading, error };
}
